import type { Dialogs } from "../../components/Dialog";
import type { Dict } from "../../lib/i18n";

/**
 * The questions the inbox and the history both ask before doing something to
 * a conversation or the person in it. Written once so the two screens cannot
 * drift into wording the same irreversible action two different ways.
 */

/** The reason for a ban, or null when the operator thought better of it. */
export async function askBan(dialogs: Dialogs, t: Dict): Promise<string | null> {
  const reason = await dialogs.prompt({
    title: t.inbox.banTitle,
    label: t.inbox.banReason,
    hint: t.inbox.banReasonHint,
    required: false,
    maxLength: 300,
    confirmLabel: t.inbox.banConfirm,
    danger: true,
  });
  return reason === null ? null : reason || t.inbox.banDefault;
}

export function askDeleteConversation(dialogs: Dialogs, t: Dict): Promise<boolean> {
  return dialogs.confirm({
    title: t.inbox.deleteTitle,
    body: t.inbox.deleteConfirm,
    confirmLabel: t.common.delete,
    danger: true,
  });
}

export function askPurge(dialogs: Dialogs, t: Dict, name: string): Promise<boolean> {
  return dialogs.confirm({
    title: t.inbox.purgeTitle,
    body: t.inbox.purgeConfirm(name),
    confirmLabel: t.context.purge,
    danger: true,
  });
}

/** A new tag, lower-cased the way the server stores it; null when cancelled or already there. */
export async function askTag(dialogs: Dialogs, t: Dict, existing: string[]): Promise<string | null> {
  const tag = await dialogs.prompt({
    title: t.conv.tagAdd,
    label: t.common.tag,
    maxLength: 40,
    confirmLabel: t.common.add,
  });
  if (!tag) return null;
  const clean = tag.toLowerCase();
  return existing.includes(clean) ? null : clean;
}

export async function askVisitorDetails(
  dialogs: Dialogs,
  t: Dict,
  visitor: { displayName: string | null; email: string | null },
): Promise<{ displayName: string; email: string } | null> {
  const values = await dialogs.form({
    title: t.context.editVisitorTitle,
    body: t.context.editVisitorBody,
    confirmLabel: t.common.save,
    fields: [
      { key: "displayName", label: t.context.visitorName, initial: visitor.displayName ?? "", required: false, maxLength: 120 },
      { key: "email", label: t.context.visitorEmail, type: "email", initial: visitor.email ?? "", required: false, maxLength: 200 },
    ],
  });
  if (!values) return null;
  return { displayName: values["displayName"] ?? "", email: values["email"] ?? "" };
}

/** The corrected text of a message, or null when nothing should change. */
export async function askMessageEdit(
  dialogs: Dialogs,
  t: Dict,
  message: { kind: string; body: string },
): Promise<string | null> {
  const body = await dialogs.prompt({
    title: t.conv.editMessageTitle,
    label: message.kind === "note" ? t.composer.note : t.composer.reply,
    // A note is the team's own; only a reply is something the customer saw.
    hint: message.kind === "note" ? undefined : t.conv.editMessageHint,
    initial: message.body,
    multiline: true,
    maxLength: 4000,
    confirmLabel: t.common.save,
  });
  return body === null || body === message.body ? null : body;
}

export function askMessageDelete(dialogs: Dialogs, t: Dict, message: { kind: string }): Promise<boolean> {
  return dialogs.confirm({
    title: t.conv.deleteMessageTitle,
    body: message.kind === "note" ? t.conv.deleteNoteBody : t.conv.deleteMessageBody,
    confirmLabel: t.common.delete,
    danger: true,
  });
}
